/* ==========================================================================
   <hero-media> — background video controller
   - silent autoplay on loop with an image poster/fallback
   - optional play/pause control per video
   - respects prefers-reduced-motion
   - pauses while off-screen to save resources
   - supports multiple videos in one section (e.g. V4 panel + split)
   ========================================================================== */
(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class VideoControl {
    constructor(videos, toggle) {
      this.videos = videos;
      this.toggle = toggle;
      // Paused state we want to preserve across intersection changes.
      this.userPaused = REDUCED_MOTION;

      videos.forEach((video) => {
        video.addEventListener('play', () => {
          if (video === this._active()) this._setState(true);
        });
        video.addEventListener('pause', () => {
          if (video === this._active()) this._setState(false);
        });
      });

      if (toggle) {
        toggle.addEventListener('click', () => {
          const active = this._active();
          if (active.paused) {
            this.userPaused = false;
            this._play(active);
          } else {
            this.userPaused = true;
            this.videos.forEach((video) => video.pause());
          }
        });
      }

      this._mq = window.matchMedia('(max-width: 749px)');
      this._onMq = () => this._sync();
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);

      const observed = videos[0].closest('.hero__media, .hero__panel-media') || videos[0];
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.userPaused) this._sync();
              } else {
                this.videos.forEach((video) => video.pause());
              }
            });
          },
          { threshold: 0.15 }
        );
        this._observer.observe(observed);
      } else if (!this.userPaused) {
        this._sync();
      }

      this._sync();
    }

    _active() {
      if (this.videos.length === 1) return this.videos[0];
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const wanted = mobile ? 'hero__video--mobile' : 'hero__video--desktop';
      return this.videos.find((video) => video.classList.contains(wanted)) || this.videos[0];
    }

    _sync() {
      const active = this._active();
      this.videos.forEach((video) => {
        if (video !== active) video.pause();
      });
      if (!this.userPaused) this._play(active);
    }

    _play(video) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          /* autoplay can be blocked — leave the poster visible */
        });
      }
    }

    _setState(playing) {
      if (!this.toggle) return;
      this.toggle.dataset.playing = playing ? 'true' : 'false';
      this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    destroy() {
      if (this._observer) this._observer.disconnect();
      if (this._mq && this._onMq && this._mq.removeEventListener) {
        this._mq.removeEventListener('change', this._onMq);
      }
    }
  }

  class HeroMedia extends HTMLElement {
    connectedCallback() {
      this.controls = [];
      const containers = new Set();
      this.querySelectorAll('.hero__video').forEach((video) => {
        containers.add(video.closest('.hero__media, .hero__panel-media') || this);
      });
      containers.forEach((container) => {
        const videos = Array.from(container.querySelectorAll(':scope > .hero__video, :scope > .hero__video--desktop, :scope > .hero__video--mobile'));
        const list = videos.length ? videos : Array.from(container.querySelectorAll('.hero__video'));
        if (!list.length) return;
        const toggle = container.querySelector('[data-hero-toggle]');
        this.controls.push(new VideoControl(list, toggle));
      });
    }

    disconnectedCallback() {
      this.controls.forEach((control) => control.destroy());
    }
  }

  if (!customElements.get('hero-media')) {
    customElements.define('hero-media', HeroMedia);
  }
})();
